import React, { useState, useEffect } from 'react';
import { Trophy, RotateCcw, AlertTriangle, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiWorker } from '../../api/worker';
import { useCurrentUser } from '../../contexts/AuthContext';
import { useTenant } from '../../contexts/TenantContext';
import toast from 'react-hot-toast';
import { GameLeaderboardScopeTabs } from './common/GameLeaderboardScopeTabs';

const WORDS = [
  'BOARD', 'CHAIR', 'VOTES', 'GAVEL', 'RULES', 'PROXY', 'AUDIT', 'GRANT', 
  'QUOTA', 'BYLAW', 'CLERK', 'FUNDS', 'EVENT', 'DONOR', 'ROLES', 'TASKS', 
  'STAFF', 'ADMIN', 'MEETS', 'SHEET', 'ROBOT', 'MONEY', 'POWER', 'SLACK',
  'TEAMS', 'MEMOS', 'FORMS', 'DRAFT', 'PITCH', 'BRIEF'
];

const MAX_GUESSES = 6;
const WORD_LENGTH = 5;

type LetterState = 'correct' | 'present' | 'absent' | 'empty';

interface Guess {
  word: string;
  states: LetterState[];
}

interface LeaderboardEntry {
  playerName: string;
  score: number;
  guessesUsed?: number;
  date?: string;
  metadata?: Record<string, any>;
}

export function OrgleGame() {
  const { isSignedIn, user } = useCurrentUser();
  const queryClient = useQueryClient();
  const [targetWord, setTargetWord] = useState('');
  const [guesses, setGuesses] = useState<Guess[]>([]);
  const [currentGuess, setCurrentGuess] = useState('');
  const [gameStatus, setGameStatus] = useState<'playing' | 'won' | 'lost'>('playing');
  const [errorMsg, setErrorMsg] = useState('');
  const { node, isGlobal } = useTenant();
  const hasActiveNode = Boolean(node && !isGlobal && node.id !== 'platform-root');
  const nodeName = hasActiveNode ? (node?.name || 'Organization') : '';
  const [leaderboardScope, setLeaderboardScope] = useState<'world' | 'node'>('world');
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [playerName, setPlayerName] = useState<string>(() => user?.username || user?.fullName || user?.firstName || '');
  const [scoreSubmitted, setScoreSubmitted] = useState<boolean>(false);

  // Fill playerName from the auth user once it's available (render-phase adjustment;
  // setState during render with a previous-value guard is the sanctioned pattern).
  const [playerNameUser, setPlayerNameUser] = useState(user);
  if (user && !playerName && user !== playerNameUser) {
    setPlayerNameUser(user);
    setPlayerName(user.username || user.fullName || user.firstName || '');
  }

  // Leaderboard Query
  const { data: leaderboardRaw } = useQuery<any>({
    queryKey: ['orgle-leaderboard', leaderboardScope, leaderboardScope === 'node' ? node?.id : 'world'],
    queryFn: async () => {
      try {
        const params: any = {};
        if (leaderboardScope === 'node' && hasActiveNode && node?.id) {
          params.nodeId = node.id;
        }
        const res = await apiWorker.get('/api/games/orgle/leaderboard', { params, silent: true });
        return res.data;
      } catch {
        return [];
      }
    },
    staleTime: 30000,
  });

  const leaderboardData: LeaderboardEntry[] = Array.isArray(leaderboardRaw)
    ? leaderboardRaw
    : Array.isArray(leaderboardRaw?.content)
      ? leaderboardRaw.content
      : Array.isArray(leaderboardRaw?.data)
        ? leaderboardRaw.data
        : [];

  const [localLeaderboard, setLocalLeaderboard] = useState<LeaderboardEntry[]>(() => {
    if (typeof window === 'undefined') return [];
    try {
      return JSON.parse(localStorage.getItem('orgle_leaderboard') || '[]');
    } catch {
      return [];
    }
  });

  const leaderboard = (leaderboardData && leaderboardData.length > 0) ? leaderboardData : localLeaderboard;

  // Submit Score Mutation
  const submitScoreMutation = useMutation({
    mutationFn: async (scoreData: { playerName: string; score: number; guessesUsed: number }) => {
      return await apiWorker.post('/api/games/orgle/leaderboard', {
        playerName: scoreData.playerName,
        score: scoreData.score,
        game: 'orgle',
        nodeId: hasActiveNode ? node?.id : undefined,
        metadata: {
          word: targetWord,
          guessesUsed: scoreData.guessesUsed
        }
      }, { silent: true });
    },
    onSuccess: (res: any) => {
      setScoreSubmitted(true);
      const isNewHigh = res?.data?.isNewHighScore;
      toast.success(isNewHigh ? '🏆 New High Score Recorded!' : 'Score recorded successfully!');
      queryClient.invalidateQueries({ queryKey: ['orgle-leaderboard'] });
    },
    onError: (err: any) => {
      if (err?.response?.status === 401) {
        toast.error('Sign in required to save score to global leaderboard.', { id: 'orgle-401' });
      } else {
        toast.error('Could not connect to leaderboard server.');
      }
    }
  });

  const initGame = () => {
    const randomWord = WORDS[Math.floor(Math.random() * WORDS.length)];
    setTargetWord(randomWord);
    setGuesses([]);
    setCurrentGuess('');
    setGameStatus('playing');
    setErrorMsg('');
    setScoreSubmitted(false);
  };

  useEffect(() => {
    // Initialize the first game off the effect's synchronous path so the initial
    // setStates land in a microtask within the same commit window.
    queueMicrotask(() => initGame());
  }, []);

  const submitGuess = () => {
    if (currentGuess.length !== WORD_LENGTH) {
      setErrorMsg('Not enough letters');
      return;
    }

    const newGuesses = [...guesses];
    const states: LetterState[] = Array(WORD_LENGTH).fill('absent');
    const targetLetters = targetWord.split('');

    // First pass: mark correct letters
    for (let i = 0; i < WORD_LENGTH; i++) {
      if (currentGuess[i] === targetLetters[i]) {
        states[i] = 'correct';
        targetLetters[i] = null as any; // mark as used
      }
    }

    // Second pass: mark present letters
    for (let i = 0; i < WORD_LENGTH; i++) {
      if (states[i] !== 'correct' && targetLetters.includes(currentGuess[i])) {
        states[i] = 'present';
        const index = targetLetters.indexOf(currentGuess[i]);
        targetLetters[index] = null as any; // mark as used
      }
    }

    newGuesses.push({ word: currentGuess, states });
    setGuesses(newGuesses);
    setCurrentGuess('');

    if (currentGuess === targetWord) {
      setGameStatus('won');
      const earnedScore = (MAX_GUESSES - newGuesses.length + 1) * 100;
      
      // Save locally
      try {
        const name = playerName.trim() || (isSignedIn ? (user?.username || user?.fullName || 'Speller') : 'Guest');
        const entry: LeaderboardEntry = {
          playerName: name,
          score: earnedScore,
          guessesUsed: newGuesses.length,
          date: new Date().toISOString().split('T')[0]
        };
        const updated = [...localLeaderboard, entry]
          .sort((a, b) => b.score - a.score)
          .slice(0, 20);
        setLocalLeaderboard(updated);
        localStorage.setItem('orgle_leaderboard', JSON.stringify(updated));
      } catch {
        // Best-effort leaderboard save; ignore storage failures.
      }
    } else if (newGuesses.length >= MAX_GUESSES) {
      setGameStatus('lost');
    }
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (gameStatus !== 'playing') return;

      if (e.key === 'Enter') {
        submitGuess();
      } else if (e.key === 'Backspace') {
        setCurrentGuess(prev => prev.slice(0, -1));
        setErrorMsg('');
      } else if (/^[a-zA-Z]$/.test(e.key) && currentGuess.length < WORD_LENGTH) {
        setCurrentGuess(prev => (prev + e.key).toUpperCase());
        setErrorMsg('');
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentGuess, gameStatus, targetWord]);

  const calculatedScore = gameStatus === 'won' ? (MAX_GUESSES - guesses.length + 1) * 100 : 0;

  const handleScoreSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!playerName.trim()) {
      toast.error('Please enter your name.');
      return;
    }
    submitScoreMutation.mutate({
      playerName: playerName.trim(),
      score: calculatedScore,
      guessesUsed: guesses.length
    });
  };

  const getKeyboardState = (key: string): LetterState => {
    let bestState: LetterState = 'empty';
    for (const guess of guesses) {
      for (let i = 0; i < WORD_LENGTH; i++) {
        if (guess.word[i] === key) {
          const state = guess.states[i];
          if (state === 'correct') return 'correct'; // best possible state
          if (state === 'present') bestState = 'present';
          if (state === 'absent' && bestState === 'empty') bestState = 'absent';
        }
      }
    }
    return bestState;
  };

  const renderGrid = () => {
    const rows = [];
    for (let i = 0; i < MAX_GUESSES; i++) {
      const isCurrentRow = i === guesses.length;
      const guess = guesses[i];
      const word = isCurrentRow ? currentGuess.padEnd(WORD_LENGTH, ' ') : (guess ? guess.word : ' '.repeat(WORD_LENGTH));
      const states = guess ? guess.states : Array(WORD_LENGTH).fill('empty');

      const cells = [];
      for (let j = 0; j < WORD_LENGTH; j++) {
        const letter = word[j];
        const state = states[j];
        
        let bgColor = 'bg-white dark:bg-black';
        let borderColor = 'border-black/20 dark:border-white/20';
        let textColor = 'text-black dark:text-white';

        if (state === 'correct') {
          bgColor = 'bg-green-500';
          borderColor = 'border-green-600';
          textColor = 'text-white';
        } else if (state === 'present') {
          bgColor = 'bg-yellow-400';
          borderColor = 'border-yellow-500';
          textColor = 'text-black';
        } else if (state === 'absent') {
          bgColor = 'bg-gray-400 dark:bg-gray-700';
          borderColor = 'border-gray-500 dark:border-gray-600';
          textColor = 'text-white';
        } else if (letter !== ' ') {
          borderColor = 'border-black dark:border-white';
        }

        cells.push(
          <div 
            key={j} 
            className={`w-12 h-12 sm:w-16 sm:h-16 flex items-center justify-center text-2xl sm:text-3xl font-black uppercase border-2 ${bgColor} ${borderColor} ${textColor}`}
          >
            {letter.trim()}
          </div>
        );
      }
      rows.push(
        <div key={i} className="flex gap-2">
          {cells}
        </div>
      );
    }
    return rows;
  };

  const keyboardRows = [
    ['Q', 'W', 'E', 'R', 'T', 'Y', 'U', 'I', 'O', 'P'],
    ['A', 'S', 'D', 'F', 'G', 'H', 'J', 'K', 'L'],
    ['ENTER', 'Z', 'X', 'C', 'V', 'B', 'N', 'M', 'BACKSPACE']
  ];

  return (
    <div className="w-full max-w-lg mx-auto font-sans">
      <div className="relative border-8 border-black dark:border-white bg-[#F5F5F0] dark:bg-[#0A0A0A] p-6 sm:p-8">
        {/* Decorative Bauhaus Accent */}
        <div className="absolute -bottom-4 -right-4 w-full h-full bg-[#0044FF] -z-10" />
        
        <div className="flex items-center justify-between mb-4">
          <div className="flex-1" />
          <div className="text-center">
            <h2 className="text-4xl sm:text-5xl font-black font-display uppercase tracking-tighter text-black dark:text-white">
              Orgle
            </h2>
            <p className="text-xs font-mono mt-1 text-black/60 dark:text-white/60">
              THE 5-LETTER BUREAUCRACY GAME
            </p>
          </div>
          <div className="flex-1 flex justify-end">
            <button
              onClick={() => setShowLeaderboard(true)}
              className="p-2 border-2 border-black dark:border-white bg-white dark:bg-black hover:bg-[#FFD600] text-black dark:text-white hover:text-black transition-colors"
              title="View Leaderboard"
            >
              <Trophy className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="flex flex-col items-center gap-2 mb-8">
          {renderGrid()}
        </div>

        {errorMsg && (
          <div className="flex items-center justify-center gap-2 text-red-600 dark:text-red-400 font-bold mb-4 font-mono">
            <AlertTriangle className="w-5 h-5" />
            <span>{errorMsg}</span>
          </div>
        )}

        <div className="flex flex-col gap-2 items-center">
          {keyboardRows.map((row, i) => (
            <div key={i} className="flex gap-1 sm:gap-2">
              {row.map(key => {
                const state = getKeyboardState(key);
                let btnBg = 'bg-gray-200 dark:bg-gray-800';
                let btnText = 'text-black dark:text-white';
                
                if (state === 'correct') {
                  btnBg = 'bg-green-500';
                  btnText = 'text-white';
                } else if (state === 'present') {
                  btnBg = 'bg-yellow-400';
                  btnText = 'text-black';
                } else if (state === 'absent') {
                  btnBg = 'bg-gray-400 dark:bg-gray-700';
                  btnText = 'text-white';
                }

                return (
                  <button
                    key={key}
                    onClick={() => {
                      if (key === 'ENTER') {
                        submitGuess();
                      } else if (key === 'BACKSPACE') {
                        setCurrentGuess(prev => prev.slice(0, -1));
                        setErrorMsg('');
                      } else if (currentGuess.length < WORD_LENGTH) {
                        setCurrentGuess(prev => prev + key);
                        setErrorMsg('');
                      }
                    }}
                    className={`${
                      key === 'ENTER' || key === 'BACKSPACE' ? 'px-2 sm:px-4 text-xs sm:text-sm' : 'w-8 sm:w-10 text-sm sm:text-lg'
                    } h-12 sm:h-14 font-bold border-2 border-black dark:border-white ${btnBg} ${btnText} uppercase transition-transform active:scale-95`}
                  >
                    {key === 'BACKSPACE' ? 'DEL' : key}
                  </button>
                );
              })}
            </div>
          ))}
        </div>

        {gameStatus !== 'playing' && (
          <div className="absolute inset-0 bg-black/85 backdrop-blur-sm flex flex-col items-center justify-center p-6 sm:p-8 text-center z-10 overflow-y-auto">
            {gameStatus === 'won' ? (
              <Trophy className="w-16 h-16 text-[#FFD600] mb-3" />
            ) : (
              <div className="text-4xl mb-3 font-black text-white">X_X</div>
            )}
            
            <h3 className="text-3xl sm:text-4xl font-black text-white uppercase mb-1">
              {gameStatus === 'won' ? 'Motion Passed!' : 'Motion Failed!'}
            </h3>
            
            <p className="text-base text-white/80 mb-2 font-mono">
              The word was <span className="font-black text-[#FFD600]">{targetWord}</span>
            </p>

            {gameStatus === 'won' && (
              <p className="text-sm font-mono text-cyan-300 font-bold mb-4">
                Score: {calculatedScore} pts ({guesses.length}/{MAX_GUESSES} guesses)
              </p>
            )}

            {/* Score Submission or Guest Sign-in */}
            {gameStatus === 'won' && (
              isSignedIn ? (
                !scoreSubmitted ? (
                  <form onSubmit={handleScoreSubmit} className="w-full max-w-xs space-y-2 mb-6">
                    <input
                      type="text"
                      placeholder="Enter Player Name..."
                      value={playerName}
                      onChange={(e) => setPlayerName(e.target.value)}
                      className="w-full px-3 py-2 bg-neutral-900 border-2 border-white text-white font-mono text-sm text-center focus:outline-none focus:border-[#FFD600]"
                    />
                    <button
                      type="submit"
                      disabled={!playerName.trim() || submitScoreMutation.isPending}
                      className="w-full py-2 bg-[#0044FF] hover:bg-[#FFD600] text-white hover:text-black font-bold uppercase tracking-wider border-2 border-white text-xs disabled:opacity-50 transition-colors"
                    >
                      {submitScoreMutation.isPending ? 'SAVING...' : 'SAVE HIGH SCORE'}
                    </button>
                  </form>
                ) : (
                  <p className="text-emerald-400 font-mono text-xs mb-6">✓ Score recorded on global leaderboard!</p>
                )
              ) : (
                <div className="w-full max-w-xs p-3 bg-neutral-900 border-2 border-white/60 text-center mb-6">
                  <p className="text-[11px] text-gray-300 font-mono mb-1">Score saved locally.</p>
                  <p className="text-xs font-bold text-white mb-2">Sign in to save your score to the global leaderboard.</p>
                  <Link
                    to="/sign-in"
                    className="block w-full py-2 bg-[#FFD600] hover:bg-white text-black font-bold uppercase tracking-wider text-xs border-2 border-black transition-colors"
                  >
                    Sign In to Save Score
                  </Link>
                </div>
              )
            )}

            <div className="flex gap-3">
              <button
                onClick={initGame}
                className="flex items-center gap-2 bg-[#FFD600] text-black font-black uppercase tracking-wider py-3 px-6 border-2 border-black hover:bg-white transition-colors text-sm"
              >
                <RotateCcw className="w-4 h-4" />
                Play Again
              </button>
              <button
                onClick={() => setShowLeaderboard(true)}
                className="flex items-center gap-2 bg-white text-black font-black uppercase tracking-wider py-3 px-6 border-2 border-black hover:bg-[#0044FF] hover:text-white transition-colors text-sm"
              >
                <Trophy className="w-4 h-4" />
                Scores
              </button>
            </div>
          </div>
        )}

        {/* Leaderboard Modal */}
        {showLeaderboard && (
          <div 
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80"
            onClick={() => setShowLeaderboard(false)}
          >
            <div 
              className="bg-[#F5F5F0] dark:bg-[#0A0A0A] border-8 border-black dark:border-white p-6 w-full max-w-md shadow-[8px_8px_0px_#0044FF]"
              onClick={e => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4 border-b-2 border-black dark:border-white pb-2">
                <h3 className="text-xl font-black font-display uppercase tracking-tight text-black dark:text-white flex items-center gap-2">
                  <Trophy className="w-5 h-5 text-[#0044FF]" /> ORGLE LEADERBOARD
                </h3>
                <button
                  onClick={() => setShowLeaderboard(false)}
                  className="p-1 hover:bg-black/10 dark:hover:bg-white/10 text-black dark:text-white font-black"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {hasActiveNode && (
                <GameLeaderboardScopeTabs
                  scope={leaderboardScope}
                  onScopeChange={setLeaderboardScope}
                  nodeName={nodeName}
                />
              )}

              {leaderboard.length === 0 ? (
                <p className="text-gray-500 text-sm text-center py-8 font-mono">No scores yet. Be the first to win!</p>
              ) : (
                <div className="space-y-2 max-h-80 overflow-y-auto">
                  {leaderboard.map((entry, i) => (
                    <div
                      key={i}
                      className={`flex items-center justify-between py-2 px-3 border-2 border-black dark:border-white ${
                        i === 0 ? 'bg-[#FFD600] text-black font-bold' :
                        i === 1 ? 'bg-white dark:bg-neutral-900 text-black dark:text-white' :
                        'bg-white/50 dark:bg-neutral-900/50 text-black dark:text-white'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <span className="font-mono font-black text-sm w-5">
                          {i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `${i + 1}`}
                        </span>
                        <span className="font-bold text-sm">{entry.playerName}</span>
                      </div>
                      <div className="text-right font-mono text-xs font-bold">
                        <span>{entry.score} pts</span>
                        {entry.guessesUsed && (
                          <span className="text-[10px] text-gray-500 block">
                            {entry.guessesUsed} {entry.guessesUsed === 1 ? 'guess' : 'guesses'}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
